/**
 * The colours the weather is drawn in, as theme tokens.
 *
 * A verdict is how a week sits against a plant's range, and it says two
 * things by colour: which way (heat and warm, cool and cold, water, or all
 * good) and how far (a tint for outside where it thrives, a solid for past
 * what it survives). These used to be Tailwind's stock pastels by palette
 * name, the same on every theme, which on Field notes' kraft paper looked
 * like another app. Each theme now defines its own (see style.css), and
 * everything that draws a verdict asks here: the chips, the week grid, the
 * comfort scale, the frost lines, the Plants page's weather block.
 *
 * Which token a mark, an item or a line is drawn in is the server's: every
 * one the server sends carries its token, read from its condition_colour
 * table (batch 13a of the architecture audit, A83). The app maps no palette
 * name or reason code to a token: this file only turns a token into the
 * theme's classes.
 *
 * Every class is written out in full, because Tailwind only ships the
 * classes it can see in the source.
 *
 * Used by: utils/outlookSeverity and most of features/plant-list's weather
 * drawing (WeekGrid, WeekState, PlantMarks, the conditions bar, the filter
 * sheet), Today's Coming up, the Weather page's frost card and
 * Seasons' Recent and coming up.
 *
 * @module theme/verdictTokens
 */

/** One family of verdict colour. */
export type VerdictToken = 'heat' | 'warm' | 'good' | 'cool' | 'cold' | 'water';

/** Ink: text and marks in the token's colour. */
export const TOKEN_TEXT: Record<VerdictToken, string> = {
  heat: 'text-verdict-heat',
  warm: 'text-verdict-warm',
  good: 'text-verdict-good',
  cool: 'text-verdict-cool',
  cold: 'text-verdict-cold',
  water: 'text-verdict-water',
};

/** Tint: a cell or chip outside where the plant thrives, with its ink. */
export const TOKEN_TINT: Record<VerdictToken, string> = {
  heat: 'bg-verdict-heat-tint text-verdict-heat',
  warm: 'bg-verdict-warm-tint text-verdict-warm',
  good: 'bg-verdict-good-tint text-verdict-good',
  cool: 'bg-verdict-cool-tint text-verdict-cool',
  cold: 'bg-verdict-cold-tint text-verdict-cold',
  water: 'bg-verdict-water-tint text-verdict-water',
};

/** Solid: past what the plant survives, in the theme's light-on-dark. */
export const TOKEN_SOLID: Record<VerdictToken, string> = {
  heat: 'bg-verdict-heat-solid text-verdict-on-solid',
  warm: 'bg-verdict-warm-solid text-verdict-on-solid',
  good: 'bg-verdict-good-solid text-verdict-on-solid',
  cool: 'bg-verdict-cool-solid text-verdict-on-solid',
  cold: 'bg-verdict-cold-solid text-verdict-on-solid',
  water: 'bg-verdict-water-solid text-verdict-on-solid',
};

/** A solid dot or fill without its text colour, for a mark on its own. */
export const TOKEN_FILL: Record<VerdictToken, string> = {
  heat: 'bg-verdict-heat-solid',
  warm: 'bg-verdict-warm-solid',
  good: 'bg-verdict-good-solid',
  cool: 'bg-verdict-cool-solid',
  cold: 'bg-verdict-cold-solid',
  water: 'bg-verdict-water-solid',
};

/** A left-hand stripe in the token's ink. */
export const TOKEN_BORDER: Record<VerdictToken, string> = {
  heat: 'border-verdict-heat',
  warm: 'border-verdict-warm',
  good: 'border-verdict-good',
  cool: 'border-verdict-cool',
  cold: 'border-verdict-cold',
  water: 'border-verdict-water',
};

/**
 * A ring in the token's ink, for a grid cell whose verdict is open.
 *
 * Not the cell's own text colour: on a solid cell that is the light
 * on-solid ink, and a ring in it vanished against the card.
 */
export const TOKEN_RING: Record<VerdictToken, string> = {
  heat: 'ring-verdict-heat',
  warm: 'ring-verdict-warm',
  good: 'ring-verdict-good',
  cool: 'ring-verdict-cool',
  cold: 'ring-verdict-cold',
  water: 'ring-verdict-water',
};
